import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";

const TOTAL_STEPS = 6;
const SESSION_KEY = "vatExitWizard.stepDisplayCurrent";

const VAT_RATES = [
  { value: "20.00", label: "20 %" },
  { value: "10.00", label: "10 %" },
  { value: "5.50", label: "5,5 %" },
  { value: "2.10", label: "2,1 %" },
  { value: "0.00", label: "0 %" },
];

const ALLOWED_START_MODES = [
  "anticipation_threshold",
  "anticipation_cas1",
  "urgent_cas2",
  "voluntary",
];

const REGIME_LABELS = {
  simplified_ca12: "CA12 — réel simplifié (annuel)",
  normal_ca3: "CA3 — réel normal",
};

const PERIODICITY_LABELS = {
  monthly: "mensuelle",
  quarterly: "trimestrielle",
};

const INTRACOM_PILL = {
  none: { label: "non demandé", className: "nm-pill nm-pill--gray" },
  requested: { label: "requested", className: "nm-pill nm-pill--ochre" },
  received: { label: "received", className: "nm-pill nm-pill--olive" },
  not_required: { label: "non requis", className: "nm-pill nm-pill--gray" },
};

const EUR_FORMAT = new Intl.NumberFormat("fr-FR", {
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 0,
});

const DATE_FORMAT = new Intl.DateTimeFormat("fr-FR", {
  day: "numeric",
  month: "long",
  year: "numeric",
});

export default class VatExitWizardController extends BaseController {
  static targets = [
    "errorBanner", "errorMessage",
    "progress",
    "articlesTable",
    "step3CaHt", "step3VatCollected", "step3VatDeductible", "step3VatNet",
    "step3PerMonthHint", "step3SwitchDate",
    "tab",
    "devisCount", "jalonsCount", "recurrentesCount",
    "devisPanel", "jalonsPanel", "recurrentesPanel",
    "devisTable", "jalonsTable", "recurrentesTable",
    "intracomInput", "intracomRequestBtn", "intracomStatusBlock",
    "recapTrigger", "recapMapping", "recapRegime", "recapImpact",
    "recapEngagements", "recapIntracom",
    "proseSwitchDate", "proseSwitchYear", "proseRefYear",
    "proseCumulDate", "proseArticlesCount",
  ];

  connect() {
    this._currentStep = 0;
    this._state = null;
    this._articles = [];
    this._step3 = null;
    this._step4 = null;
    this._stepBtns = Array.from(this.element.querySelectorAll(".nm-wizard__step-btn"));
    this._stepItems = Array.from(this.element.querySelectorAll(".nm-wizard__step"));
    this._panels = Array.from(this.element.querySelectorAll("[data-wizard-panel]"));
    this._prevBtn = this.element.querySelector("[data-wizard-prev]");
    this._nextBtn = this.element.querySelector("[data-wizard-next]");
    this._submitBtn = this.element.querySelector("[data-wizard-submit]");
    this._bootstrap();
  }

  async _bootstrap() {
    try {
      let state = await api.get("/me/vat-exit-wizard/state");
      if (!state.startedAt) {
        const mode = this._readStartModeFromUrl();
        if (!mode) {
          this._showError(
            "Le wizard de sortie de franchise TVA n'est pas démarré. Revenez à la page TVA et activez la bascule depuis l'écran dédié.",
          );
          return;
        }
        await api.post("/me/vat-exit-wizard/start", { mode });
        state = await api.get("/me/vat-exit-wizard/state");
      }
      this._state = state;
      await this._loadArticles();
      await this._loadStep3();
      await this._loadStep4();
      this._renderAll();
      this._restoreDisplayStep();
    } catch (error) {
      this._handleApiError(error, "Impossible de charger l'état du wizard.");
    }
  }

  _readStartModeFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const mode = params.get("mode");
    return ALLOWED_START_MODES.includes(mode) ? mode : null;
  }

  // --- Loading ---------------------------------------------------------------

  async _loadArticles() {
    try {
      const resp = await api.get("/articles", {
        "filter[status]": "active",
        perPage: "200",
      });
      this._articles = Array.isArray(resp.data) ? resp.data : [];
    } catch (error) {
      this._articles = [];
      this._handleApiError(error, "Impossible de charger les articles.");
    }
  }

  async _loadStep3() {
    try {
      this._step3 = await api.get("/me/vat-exit-wizard/step3-impact");
    } catch (error) {
      this._step3 = null;
    }
  }

  async _loadStep4() {
    try {
      this._step4 = await api.get("/me/vat-exit-wizard/step4-engagements");
    } catch (error) {
      this._step4 = { quotes: [], milestones: [], recurrings: [] };
    }
  }

  // --- Render ----------------------------------------------------------------

  _renderAll() {
    this._renderProse();
    this._renderStep1();
    this._renderStep3();
    this._renderStep4();
    this._renderStep5();
    this._renderRecap();
  }

  /**
   * Injecte dans la prose des panneaux les valeurs réelles du dossier :
   * date de bascule (step3), année de bascule, année de référence (en cours),
   * date du jour pour le cumul YTD et nombre d'articles actifs. Les valeurs
   * non exposées par le backend (date de confirmation, deadline SIE, montant
   * exact du seuil franchi) restent formulées de façon générique dans le HTML.
   */
  _renderProse() {
    const switchLabel = this._step3?.switchDateLabel
      || (this._step3?.switchDate
        ? DATE_FORMAT.format(new Date(`${this._step3.switchDate}T00:00:00`))
        : "—");
    const switchYear = this._step3?.switchDate
      ? this._step3.switchDate.slice(0, 4)
      : "—";
    const refYear = String(new Date().getFullYear());

    this.proseSwitchDateTargets.forEach((el) => { el.textContent = switchLabel; });
    this.proseSwitchYearTargets.forEach((el) => { el.textContent = switchYear; });
    this.proseRefYearTargets.forEach((el) => { el.textContent = refYear; });
    if (this.hasProseCumulDateTarget) {
      this.proseCumulDateTarget.textContent = DATE_FORMAT.format(new Date());
    }
    if (this.hasProseArticlesCountTarget) {
      this.proseArticlesCountTarget.textContent = String(this._articles.length);
    }
  }

  _renderStep1() {
    if (!this.hasArticlesTableTarget) return;
    const tbody = this.articlesTableTarget.querySelector("tbody")
      ?? this.articlesTableTarget;
    tbody.innerHTML = "";
    this._articles.forEach((article, idx) => {
      const frag = this.cloneTemplate("tpl-article-row");
      if (!frag) return;
      const vatSelectId = `wt-vat-rate-${idx}`;
      this.fillSlots(frag, {
        reference: article.reference ?? "—",
        label: article.label ?? "—",
        priceHt: EUR_FORMAT.format(Number(article.priceExclVat ?? 0)),
        vatLabelAria: `Taux de TVA pour ${article.label ?? "l'article"}`,
      });
      const labelEl = frag.querySelector('[data-slot="vatLabelAria"]');
      if (labelEl) labelEl.setAttribute("for", vatSelectId);
      const select = frag.querySelector('[data-slot="vatSelect"]');
      if (select) {
        select.id = vatSelectId;
        select.dataset.articleId = String(article.id);
        VAT_RATES.forEach((rate) => {
          const opt = document.createElement("option");
          opt.value = rate.value;
          opt.textContent = rate.label;
          if (this._matchVatRate(article.vatRate, rate.value)) opt.selected = true;
          select.appendChild(opt);
        });
        if (!VAT_RATES.some((r) => this._matchVatRate(article.vatRate, r.value))) {
          const [first] = select.options;
          if (first) first.selected = true;
        }
      }
      tbody.appendChild(frag);
    });
  }

  _matchVatRate(rawArticleRate, candidate) {
    const a = Number(rawArticleRate);
    const b = Number(candidate);
    if (Number.isNaN(a) || Number.isNaN(b)) return false;
    return Math.abs(a - b) < 0.001;
  }

  _renderStep3() {
    if (!this._step3) return;
    const set = (target, value, bold = false) => {
      if (!target) return;
      if (bold) {
        target.innerHTML = "";
        const strong = document.createElement("strong");
        strong.textContent = value;
        target.appendChild(strong);
      } else {
        target.textContent = value;
      }
    };
    if (this.hasStep3CaHtTarget) {
      set(this.step3CaHtTarget, EUR_FORMAT.format(this._step3.caHtYtd ?? 0), true);
    }
    if (this.hasStep3VatCollectedTarget) {
      set(this.step3VatCollectedTarget, EUR_FORMAT.format(this._step3.vatCollectedTheoretical ?? 0), true);
    }
    if (this.hasStep3VatDeductibleTarget) {
      const deductible = Number(this._step3.vatDeductibleEstimated ?? 0);
      set(this.step3VatDeductibleTarget, `– ${EUR_FORMAT.format(deductible)}`);
    }
    if (this.hasStep3VatNetTarget) {
      set(this.step3VatNetTarget, EUR_FORMAT.format(this._step3.vatNetProjected ?? 0), true);
    }
    if (this.hasStep3PerMonthHintTarget) {
      const monthly = Number(this._step3.vatNetProjected ?? 0) / 12;
      this.step3PerMonthHintTarget.textContent = `Soit ~ ${EUR_FORMAT.format(monthly)}/mois en moyenne`;
    }
    if (this.hasStep3SwitchDateTarget) {
      const label = this._step3.switchDateLabel ?? (
        this._step3.switchDate
          ? DATE_FORMAT.format(new Date(`${this._step3.switchDate}T00:00:00`))
          : "—"
      );
      this.step3SwitchDateTarget.textContent = label;
    }
  }

  _renderStep4() {
    if (!this._step4) return;
    const data = this._step4;
    const quotes = Array.isArray(data.quotes) ? data.quotes : [];
    const milestones = Array.isArray(data.milestones) ? data.milestones : [];
    const recurrings = Array.isArray(data.recurrings) ? data.recurrings : [];
    this._renderStep4Quotes(quotes);
    this._renderStep4Milestones(milestones);
    this._renderStep4Recurrings(recurrings);
    if (this.hasDevisCountTarget) this.devisCountTarget.textContent = String(quotes.length);
    if (this.hasJalonsCountTarget) this.jalonsCountTarget.textContent = String(milestones.length);
    if (this.hasRecurrentesCountTarget) this.recurrentesCountTarget.textContent = String(recurrings.length);
  }

  _renderStep4Quotes(quotes) {
    if (!this.hasDevisTableTarget) return;
    const tbody = this.devisTableTarget.querySelector("tbody") ?? this.devisTableTarget;
    tbody.innerHTML = "";
    if (quotes.length === 0) {
      tbody.appendChild(this._buildEmptyRow(6, "Aucun engagement impacté dans cette catégorie."));
      return;
    }
    quotes.forEach((q) => {
      const frag = this.cloneTemplate("tpl-devis-row");
      if (!frag) return;
      const status = q.status ?? "issued";
      const actionLabel = status === "accepted" ? "Générer rectificatif" : "Générer version TTC";
      const action = status === "accepted" ? "supersede" : "ttc";
      const statusLabel = status === "accepted" ? "accepté" : "émis";
      const statusPillClass = status === "accepted" ? "nm-pill nm-pill--olive" : "nm-pill nm-pill--blue";
      this.fillSlots(frag, {
        number: q.number ?? "—",
        clientName: q.clientName ?? "—",
        issuedAt: q.issuedAt ? DATE_FORMAT.format(new Date(`${q.issuedAt}T00:00:00`)) : "—",
        amountHt: EUR_FORMAT.format(Number(q.amountHt ?? 0)),
        statusLabel,
        actionLabel,
      });
      const pillEl = frag.querySelector(".nm-pill");
      if (pillEl) pillEl.setAttribute("class", statusPillClass);
      const btn = frag.querySelector("button");
      if (btn) {
        btn.dataset.action = "click->vat-exit-wizard#actionQuote";
        btn.dataset.quoteId = String(q.id);
        btn.dataset.quoteAction = action;
      }
      if (q.processed) this._markRowProcessed(frag.querySelector("tr"), q.processedLabel ?? "Traité");
      tbody.appendChild(frag);
    });
  }

  _renderStep4Milestones(milestones) {
    if (!this.hasJalonsTableTarget) return;
    const tbody = this.jalonsTableTarget.querySelector("tbody") ?? this.jalonsTableTarget;
    tbody.innerHTML = "";
    if (milestones.length === 0) {
      tbody.appendChild(this._buildEmptyRow(5, "Aucun engagement impacté dans cette catégorie."));
      return;
    }
    milestones.forEach((m) => {
      const frag = this.cloneTemplate("tpl-jalon-row");
      if (!frag) return;
      this.fillSlots(frag, {
        quoteNumber: m.quoteNumber ?? "—",
        milestoneLabel: m.milestoneLabel ?? m.label ?? "—",
        dueDate: m.dueDate ? DATE_FORMAT.format(new Date(`${m.dueDate}T00:00:00`)) : "—",
        amountHt: EUR_FORMAT.format(Number(m.amountHt ?? 0)),
      });
      const tr = frag.querySelector("tr");
      if (tr) {
        const td = document.createElement("td");
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "nm-btn nm-btn--ghost nm-btn--sm";
        btn.textContent = "Archiver + cloner avec TVA";
        btn.dataset.action = "click->vat-exit-wizard#actionMilestone";
        btn.dataset.milestoneId = String(m.id);
        td.appendChild(btn);
        tr.appendChild(td);
        if (m.processed) this._markRowProcessed(tr, m.processedLabel ?? "Traité");
      }
      tbody.appendChild(frag);
    });
  }

  _renderStep4Recurrings(recurrings) {
    if (!this.hasRecurrentesTableTarget) return;
    const tbody = this.recurrentesTableTarget.querySelector("tbody") ?? this.recurrentesTableTarget;
    tbody.innerHTML = "";
    if (recurrings.length === 0) {
      tbody.appendChild(this._buildEmptyRow(5, "Aucun engagement impacté dans cette catégorie."));
      return;
    }
    recurrings.forEach((r) => {
      const frag = this.cloneTemplate("tpl-recurrente-row");
      if (!frag) return;
      this.fillSlots(frag, {
        label: r.label ?? "—",
        clientName: r.clientName ?? "—",
        periodicityLabel: r.periodicityLabel ?? PERIODICITY_LABELS[r.periodicity] ?? "—",
        amountHt: EUR_FORMAT.format(Number(r.amountHt ?? 0)),
      });
      const tr = frag.querySelector("tr");
      if (tr) {
        const td = document.createElement("td");
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "nm-btn nm-btn--ghost nm-btn--sm";
        btn.textContent = "Archiver + dupliquer avec TVA";
        btn.dataset.action = "click->vat-exit-wizard#actionRecurring";
        btn.dataset.recurringId = String(r.id);
        td.appendChild(btn);
        tr.appendChild(td);
        if (r.processed) this._markRowProcessed(tr, r.processedLabel ?? "Traité");
      }
      tbody.appendChild(frag);
    });
  }

  _buildEmptyRow(colspan, message) {
    const tr = document.createElement("tr");
    const td = document.createElement("td");
    td.colSpan = colspan;
    td.className = "fs-13 text-muted";
    td.style.textAlign = "center";
    td.style.padding = "20px";
    td.textContent = message;
    tr.appendChild(td);
    return tr;
  }

  _markRowProcessed(tr, label) {
    if (!tr) return;
    tr.classList.add("is-processed");
    tr.style.opacity = "0.5";
    const btn = tr.querySelector("button");
    if (btn) {
      btn.disabled = true;
      btn.textContent = label;
    }
  }

  _renderStep5() {
    if (!this._state) return;
    const status = this._state.vatIntracomStatus ?? "none";
    if (this.hasIntracomInputTarget) {
      this.intracomInputTarget.value = this._state.vatIntracomNumber ?? "";
      this.intracomInputTarget.disabled = status === "not_required";
    }
    if (this.hasIntracomStatusBlockTarget) {
      const pillInfo = INTRACOM_PILL[status] ?? INTRACOM_PILL.none;
      this.intracomStatusBlockTarget.innerHTML = "";
      const pill = document.createElement("span");
      pill.className = pillInfo.className;
      const dot = document.createElement("span");
      dot.className = "nm-pill__dot";
      pill.appendChild(dot);
      pill.appendChild(document.createTextNode(pillInfo.label));
      this.intracomStatusBlockTarget.appendChild(pill);
      this.intracomStatusBlockTarget.appendChild(
        document.createTextNode(this._intracomStatusHint(status)),
      );
    }
    if (this.hasIntracomRequestBtnTarget) {
      this.intracomRequestBtnTarget.disabled = status !== "none";
    }
  }

  _intracomStatusHint(status) {
    switch (status) {
      case "requested": return " — courrier envoyé, n° pas encore reçu";
      case "received": return " — n° reçu et enregistré";
      case "not_required": return " — non applicable à votre situation";
      default: return " — courrier à envoyer";
    }
  }

  _renderRecap() {
    if (!this._state) return;
    const state = this._state;
    const set = (target, html) => { if (target) target.innerHTML = html; };

    if (this.hasRecapTriggerTarget) {
      set(this.recapTriggerTarget, this._formatTriggerLabel(state.mode, this._step3?.switchDateLabel));
    }
    if (this.hasRecapMappingTarget) {
      set(this.recapMappingTarget, this._formatMappingSummary());
    }
    if (this.hasRecapRegimeTarget) {
      set(this.recapRegimeTarget, this._formatRegimeSummary());
    }
    if (this.hasRecapImpactTarget) {
      set(this.recapImpactTarget, this._formatImpactSummary());
    }
    if (this.hasRecapEngagementsTarget) {
      set(this.recapEngagementsTarget, this._formatEngagementsSummary());
    }
    if (this.hasRecapIntracomTarget) {
      set(this.recapIntracomTarget, this._formatIntracomSummary());
    }
  }

  _formatTriggerLabel(mode, switchDateLabel) {
    const triggers = {
      anticipation_threshold: "Anticipation seuil",
      anticipation_cas1: "Anticipation Cas 1",
      urgent_cas2: "Urgence Cas 2",
      voluntary: "Option volontaire",
    };
    const label = triggers[mode] ?? "Mode non renseigné";
    const date = switchDateLabel
      ? `<br/><span class="muted fs-12">${this.escapeHtml(switchDateLabel)}</span>`
      : "";
    return `<strong>${this.escapeHtml(label)}</strong>${date}`;
  }

  _formatMappingSummary() {
    const articles = this._articles ?? [];
    if (articles.length === 0) return "<span class=\"muted\">Aucun article actif</span>";
    const counts = new Map();
    articles.forEach((a) => {
      const key = Number(a.vatRate ?? 0).toFixed(1).replace(".0", "");
      counts.set(key, (counts.get(key) ?? 0) + 1);
    });
    const parts = [...counts.entries()]
      .sort((a, b) => Number(b[0]) - Number(a[0]))
      .map(([rate, count]) => `${count} × ${rate} %`);
    return `<strong>${articles.length} article${articles.length > 1 ? "s" : ""} mappé${articles.length > 1 ? "s" : ""}</strong> — ${this.escapeHtml(parts.join(" · "))}`;
  }

  _formatRegimeSummary() {
    const state = this._state ?? {};
    const regime = state.vatRegimeChosen;
    if (!regime) return "<span class=\"muted\">À définir à l'étape 2</span>";
    const baseLabel = REGIME_LABELS[regime] ?? regime;
    const peri = state.vatCa3Periodicity
      ? `<br/><span class="muted fs-12">Périodicité ${this.escapeHtml(PERIODICITY_LABELS[state.vatCa3Periodicity] ?? state.vatCa3Periodicity)}</span>`
      : "";
    return `<strong>${this.escapeHtml(baseLabel)}</strong>${peri}`;
  }

  _formatImpactSummary() {
    if (!this._step3) return "<span class=\"muted\">À calculer à l'étape 3</span>";
    const net = Number(this._step3.vatNetProjected ?? 0);
    const monthly = net / 12;
    return `TVA nette à reverser : <strong>~ ${this.escapeHtml(EUR_FORMAT.format(net))}</strong><br/><span class="muted fs-12">Soit ~ ${this.escapeHtml(EUR_FORMAT.format(monthly))}/mois en moyenne</span>`;
  }

  _formatEngagementsSummary() {
    const data = this._step4 ?? { quotes: [], milestones: [], recurrings: [] };
    const q = (data.quotes ?? []).length;
    const m = (data.milestones ?? []).length;
    const r = (data.recurrings ?? []).length;
    if (q + m + r === 0) {
      return "Aucun engagement à traiter — vous pouvez passer à l'étape suivante";
    }
    return `${q} devis · ${m} jalons futurs · ${r} récurrentes — actions à réaliser depuis l'étape 4`;
  }

  _formatIntracomSummary() {
    const status = this._state?.vatIntracomStatus ?? "none";
    const info = INTRACOM_PILL[status] ?? INTRACOM_PILL.none;
    const number = this._state?.vatIntracomNumber;
    const detail = number
      ? `<br/><span class="muted fs-12">${this.escapeHtml(number)}</span>`
      : `<br/><span class="muted fs-12">Renseignable ultérieurement depuis le profil entreprise</span>`;
    return `<span class="${info.className}"><span class="nm-pill__dot"></span>${this.escapeHtml(info.label)}</span>${detail}`;
  }

  // --- Navigation ------------------------------------------------------------

  _renderStepper() {
    this._stepItems.forEach((item, i) => {
      item.classList.toggle("is-current", i === this._currentStep);
      item.classList.toggle("is-done", i < this._currentStep);
    });
    this._stepBtns.forEach((btn, i) => {
      if (i === this._currentStep) btn.setAttribute("aria-current", "step");
      else btn.removeAttribute("aria-current");
      btn.disabled = i > this._currentStep;
    });
    this._panels.forEach((p, i) => p.classList.toggle("is-active", i === this._currentStep));
    if (this._prevBtn) this._prevBtn.disabled = this._currentStep === 0;
    const isLast = this._currentStep === TOTAL_STEPS - 1;
    if (this._nextBtn) this._nextBtn.hidden = isLast;
    if (this._submitBtn) this._submitBtn.hidden = !isLast;
    if (this.hasProgressTarget) {
      this.progressTarget.textContent = `Étape ${this._currentStep + 1} sur ${TOTAL_STEPS}`;
    }
  }

  _goTo(stepIndex, { focus = true } = {}) {
    if (stepIndex < 0 || stepIndex >= TOTAL_STEPS) return;
    this._currentStep = stepIndex;
    sessionStorage.setItem(SESSION_KEY, String(stepIndex));
    this._renderStepper();
    if (focus) {
      const panel = this._panels[stepIndex];
      if (panel) {
        panel.focus({ preventScroll: true });
        panel.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }

  _restoreDisplayStep() {
    const stored = parseInt(sessionStorage.getItem(SESSION_KEY) ?? "", 10);
    const serverIndex = Math.max(0, (this._state?.currentStep ?? 1) - 1);
    const intracomPending = this._state?.currentStep === 5
      && this._state?.vatIntracomStatus !== "received";
    if (intracomPending) {
      this._goTo(4, { focus: false });
      return;
    }
    const restored = !Number.isNaN(stored) && stored >= 0 && stored < TOTAL_STEPS && stored <= serverIndex
      ? stored
      : serverIndex;
    this._goTo(restored, { focus: false });
  }

  prev() {
    if (this._currentStep === 0) return;
    this._submitStepIfNeeded(this._currentStep).then(() => {
      this._goTo(this._currentStep - 1);
    });
  }

  next() {
    if (this._currentStep >= TOTAL_STEPS - 1) return;
    this._submitStepIfNeeded(this._currentStep).then(() => {
      this._goTo(this._currentStep + 1);
    });
  }

  gotoStep(event) {
    const target = event.currentTarget;
    const raw = target?.dataset.wizardGoto ?? target?.dataset.wizardStep;
    const index = parseInt(raw ?? "", 10);
    if (!Number.isNaN(index) && index <= this._currentStep) {
      this._goTo(index);
    }
  }

  switchTab(event) {
    const tab = event.currentTarget;
    if (!tab) return;
    const targetId = tab.getAttribute("aria-controls");
    this.tabTargets.forEach((t) => {
      const isActive = t === tab;
      t.setAttribute("aria-selected", isActive ? "true" : "false");
      const panelId = t.getAttribute("aria-controls");
      const panel = panelId ? this.element.querySelector(`#${panelId}`) : null;
      if (panel) panel.hidden = panelId !== targetId;
    });
  }

  // --- Step submissions ------------------------------------------------------

  async _submitStepIfNeeded(stepIndex) {
    if (stepIndex === 0) await this._submitStep1();
    // Step 2 submission goes through tva-regime-comparator#submit + onRegimeChoiceSubmitted
    // Steps 3 and 4 are read-only / per-row actions — no batch submission
    // Step 5 number is submitted via #updateIntracomNumber on change
  }

  async _submitStep1() {
    if (!this.hasArticlesTableTarget) return;
    const selects = this.articlesTableTarget.querySelectorAll("select[data-article-id]");
    const articles = Array.from(selects).map((sel) => ({
      id: sel.dataset.articleId,
      vatRate: Number(sel.value),
    }));
    if (articles.length === 0) return;
    try {
      await api.put("/me/vat-exit-wizard/step1-articles", { articles });
    } catch (error) {
      this._handleApiError(error, "Impossible d'enregistrer les taux de TVA.");
      throw error;
    }
  }

  async onRegimeChoiceSubmitted() {
    try {
      this._state = await api.get("/me/vat-exit-wizard/state");
      await this._loadStep3();
      this._renderStep3();
      this._renderRecap();
      this._goTo(2);
    } catch (error) {
      this._handleApiError(error, "Impossible de rafraîchir l'état après le choix de régime.");
    }
  }

  // --- Step 4 actions --------------------------------------------------------

  async actionQuote(event) {
    const btn = event.currentTarget;
    const id = btn?.dataset.quoteId;
    const action = btn?.dataset.quoteAction;
    if (!id || !action) return;
    const endpoint = action === "supersede"
      ? `/me/vat-exit-wizard/step4-quote-supersede/${encodeURIComponent(id)}`
      : `/me/vat-exit-wizard/step4-quote-ttc/${encodeURIComponent(id)}`;
    await this._runStep4Action(btn, endpoint, "devis");
  }

  async actionMilestone(event) {
    const btn = event.currentTarget;
    const id = btn?.dataset.milestoneId;
    if (!id) return;
    await this._runStep4Action(
      btn,
      `/me/vat-exit-wizard/step4-milestone-regen/${encodeURIComponent(id)}`,
      "jalon",
    );
  }

  async actionRecurring(event) {
    const btn = event.currentTarget;
    const id = btn?.dataset.recurringId;
    if (!id) return;
    await this._runStep4Action(
      btn,
      `/me/vat-exit-wizard/step4-recurring-duplicate/${encodeURIComponent(id)}`,
      "récurrente",
    );
  }

  async _runStep4Action(btn, endpoint, kind) {
    btn.disabled = true;
    const originalLabel = btn.textContent;
    btn.textContent = "Traitement…";
    try {
      const resp = await api.post(endpoint);
      const tr = btn.closest("tr");
      this._markRowProcessed(tr, resp?.processedLabel ?? "Traité");
      this._decrementTabCounter(kind);
      this.showSuccess(`Action sur ${kind} effectuée.`);
    } catch (error) {
      btn.disabled = false;
      btn.textContent = originalLabel;
      this._handleApiError(error, `Action sur ${kind} impossible.`);
    }
  }

  _decrementTabCounter(kind) {
    const target = kind === "devis" ? this.devisCountTarget
      : kind === "jalon" ? this.jalonsCountTarget
      : this.recurrentesCountTarget;
    if (!target) return;
    const current = parseInt(target.textContent ?? "0", 10);
    if (!Number.isNaN(current) && current > 0) {
      target.textContent = String(current - 1);
    }
  }

  // --- Step 5 — intracom -----------------------------------------------------

  async requestIntracom(event) {
    const btn = event.currentTarget;
    btn.disabled = true;
    try {
      const resp = await api.post("/me/vat-intracom/request");
      if (resp?.pdfUrl) {
        window.open(resp.pdfUrl, "_blank", "noopener");
      }
      this._state = await api.get("/me/vat-exit-wizard/state");
      this._renderStep5();
      this._renderRecap();
      this.showSuccess("Courrier SIE généré.");
    } catch (error) {
      btn.disabled = false;
      this._handleApiError(error, "Impossible de générer le courrier SIE.");
    }
  }

  async updateIntracomNumber(event) {
    const value = (event.currentTarget.value ?? "").trim();
    if (value === "") return;
    try {
      await api.put("/me/vat-intracom/number", { number: value });
      this._state = await api.get("/me/vat-exit-wizard/state");
      this._renderStep5();
      this._renderRecap();
      this.showSuccess("Numéro intracommunautaire enregistré.");
    } catch (error) {
      this._handleApiError(error, "Numéro intracommunautaire invalide.");
    }
  }

  // --- Step 6 — submit -------------------------------------------------------

  async submit() {
    if (!this._submitBtn) return;
    this._submitBtn.disabled = true;
    const originalLabel = this._submitBtn.textContent;
    this._submitBtn.textContent = "Enregistrement…";
    try {
      await api.post("/me/vat-exit-wizard/complete");
      this._submitBtn.textContent = "Wizard finalisé ✓";
      this.showSuccess("Wizard de sortie de franchise TVA finalisé.");
    } catch (error) {
      this._submitBtn.disabled = false;
      this._submitBtn.textContent = originalLabel;
      this._handleApiError(error, "Impossible de finaliser le wizard.");
    }
  }

  // --- Error handling --------------------------------------------------------

  _handleApiError(error, fallback) {
    const message = error instanceof APIError ? (error.message || fallback) : fallback;
    this._showError(message);
  }

  _showError(message) {
    if (this.hasErrorBannerTarget) {
      if (this.hasErrorMessageTarget) this.errorMessageTarget.textContent = message;
      this.errorBannerTarget.hidden = false;
      this.errorBannerTarget.scrollIntoView({ behavior: "smooth", block: "center" });
    } else {
      this.showError(message);
    }
  }

  _hideError() {
    if (this.hasErrorBannerTarget) this.errorBannerTarget.hidden = true;
  }
}
